import React, { useState } from 'react';
import { getInspectionSignatureSources, normalizeMediaDataUrl } from '@/utils/inspectionDisplay';

/**
 * Renders responsible-party signature with normalized data URLs and readable contrast.
 * Some mobile builds export white ink on a black canvas; invert restores visibility on white UI.
 */
export default function InspectionSignatureSection({ inspection, title, alt }) {
  const { imageUrl, signerName } = getInspectionSignatureSources(inspection);
  const [invert, setInvert] = useState(false);

  if (!imageUrl && !signerName) return null;

  const handleLoad = (e) => {
    try {
      const img = e.target;
      const w = Math.min(img.naturalWidth || 0, 64);
      const h = Math.min(img.naturalHeight || 0, 64);
      if (w < 4 || h < 4) return;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      let dark = 0;
      let light = 0;
      for (let i = 0; i < data.length; i += 4) {
        const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        if (lum < 80) dark += 1;
        if (lum > 200) light += 1;
      }
      const pixels = data.length / 4;
      if (dark > pixels * 0.55 && light > pixels * 0.02) {
        setInvert(true);
      }
    } catch (_) {
      /* cross-origin or tainted canvas — keep default */
    }
  };

  return (
    <div>
      <h4 className="font-semibold mb-3">{title}</h4>
      <div className="flex items-center gap-4">
        {imageUrl && (
          <img
            src={normalizeMediaDataUrl(imageUrl)}
            alt={alt}
            onLoad={handleLoad}
            className="h-20 max-w-[280px] object-contain bg-white border rounded p-1"
            style={invert ? { filter: 'invert(1)' } : undefined}
          />
        )}
        {signerName && <span className="text-gray-600">{signerName}</span>}
      </div>
    </div>
  );
}
